<template>
  <div class="card-profile-content" style="position: relative"
       :class="{
    'not-editable':editSection !== '' && editSection !== title,
    'editable':editSection === title}">
    <b-loading :active="editSection !== '' && editSection !== title"
               animation="unset" :is-full-page="false">
      <slot>{{''|upper}}</slot>
    </b-loading>
    <template v-if="editSection!=='Profile'">
      <div>
        <div class="personal-card-full-name">
          {{firstname}} {{lastname}}
        </div>
        <ul class="personal-card-contacts">
          <li class="personal-card-contacts-item email">
            <b-icon pack="fa" size="is-small" icon="envelope"></b-icon>
            <span>{{ email }}</span>
          </li>
          <li
              v-if="phone"
              class="personal-card-contacts-item phone"
          >
            <b-icon pack="fa" size="is-small" icon="phone" class="fa-rotate-90"></b-icon>
            <span>{{ getPhoneFormatted }}</span>
          </li>
        </ul>
      </div>
      <div class="personal-card-controls">
        <button class="button is-success"
                @click="$emit('edit-card', 'Profile')">EDIT</button>
      </div>
    </template>
    <template v-else>
      <validation-observer ref="cardProfileObserver" v-slot="{ handleSubmit, reset }">
        <form @reset.prevent="setSectionNotEditable(reset)"
              @submit.prevent="handleSubmit(submitProfileSection)">
          <div class="columns">
            <div class="column">
              <field name="First Name*"
                     editable
                     v-model.trim="firstname"
                     rules="name|required|max:100" maxlength="100"></field>
              <field name="Last Name*"
                     editable
                     v-model.trim="lastname"
                     rules="name|required|max:100" maxlength="100"></field>
              <field name="Contact Email (Login)*"
                     v-model.trim="email"
                     :tooltip="'Set the email address you use to log in to Enhancify.' +
                     ' An email address can only be associated with one Enhancify' +
                     ' account at a time. \nWhen you change the email address' +
                     ' on your Enhancify account, we’ll send you an email' +
                     ' to your new email address. You\'ll need to open the email and' +
                     ' click the Confirm Email button. Until you do not complete' +
                     ' the confirmation process above, your email address will remain' +
                     ' the same in your profile.'"
                     :size="'is-large'"
                     tooltip-classes="is-gray"
                     icon-pack="fa"
                     editable
                     :message="isEmailChanged ? [emailChangeMessage] : []"
                     rules="email|required|max:250" maxlength="250"></field>
              <validation-provider
                  v-if="partner.id || (
                      contractorId && (isContractorUser || isContractorTeamUser || isSwitched)
                      )"
                  v-slot="{ errors }"
                  name="phone"
                  v-model="phone"
                  :rules="{
                    required: !isContractorTeamUser, phone_with_mask: true,
                  }">
                <b-field
                    :type="{ 'is-danger': errors.length }"
                    :value="phone"
                    :label="`Contact number${!isContractorTeamUser ? '*' : ''}`"
                    custom-class="personal-info__field-label"
                    class="personal-info__field field-input-phone">
                  <!-- todo: use ModernPhoneInput component -->
                  <b-input
                      @focusin.native="focusIn"
                      @focusout.native="focusOut"
                      ref="phoneInput"
                      class="input-phone"
                      :style="cssVars"
                      placeholder="234-567-8910"
                      v-model="phone"
                      v-cleave="masks.phone">
                  </b-input>
                  <div class="input-phone-bottom">
                    <p class="help is-danger">{{errors + [ '&nbsp;' ]}}</p>
                    <small id=help-counter class="help counter is-invisible">
                      {{rawContactNumber.length}} / 10
                    </small>
                  </div>
                </b-field>
              </validation-provider>
              <b-field></b-field>
              <div class="buttons is-pulled-right">
                <button class="button button-cancel" type="reset">
                  Cancel
                </button>
                <button class="button is-success" type="submit">Submit</button>
              </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </template>
  </div>
</template>

<script>

import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import * as USER_ACTIONS from '@/store/modules/user/actions';
import * as MUTATION from '@/store/mutations';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import * as PARTNER_ACTIONS from '@/store/modules/partner/actions';
import Field from '../../../Field.vue';

export default {
  data() {
    return {
      id: null,
      firstname: null,
      lastname: null,
      email: '',
      rawContactNumber: '',
      phone: '',
      cardFields: ['firstname', 'lastname', 'email', 'phone'],
      isEmailChanged: false,
      emailChangeMessage: 'Email will be changed after confirmation',
      countryCode: '+1',
      contractorTeamData: {},
      isContractorTeamUser: false,
      isContractorUser: false,
      init: {},
      masks: {
        phone: {
          delimiters: ['-', '-', ''],
          blocks: [3, 3, 4],
          numericOnly: true,
          onValueChanged: (e) => {
            this.rawContactNumber = e.target.rawValue;
          },
        },
      },
      objectInState: this.$store.state.partner.id
        ? this.$store.state.partner : this.$store.state.contractor,
    };
  },
  components: {
    Field,
  },
  name: 'CardProfile',
  props: {
    editSection: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: null,
    },
  },
  watch: {
    /** Hack, for run computed.stateToLocal */
    stateToLocal() {
      // pass
    },
    editSection() {
      this.preparePhone();
    },
  },

  computed: {
    ...mapState('contractor', { contractorId: (state) => state.id }),
    ...mapGetters(['isSwitched']),
    ...mapState('partner', { partner: (state) => state }),
    cssVars() {
      return {
        '--country-code': JSON.stringify(this.countryCode),
      };
    },
    getPhoneFormatted() {
      return this.phone
        .replace(/^\+\d/, '')
        .replace(/^(\d{3})(\d{3})(\d+)$/, '$1-$2-$3');
    },
    stateToLocal() {
      Object.keys(this.$store.state.contractor).forEach((key) => {
        if (key === 'phone') {
          this[key] = this.$store.state.contractor[key];
        }
      });
      if (this.partner.id) {
        Object.keys(this.partner).forEach((key) => {
          if (key === 'phone') {
            this[key] = this.partner[key];
          }
        });
      }
      return Object.keys(this.$store.state.user).forEach((key) => {
        if (key === 'firstname' && this.$store.state.user.firstname) {
          this.firstname = this.$store.state.user.firstname.substring(0, 100);
        } else if (key === 'lastname' && this.$store.state.user.lastname) {
          this.lastname = this.$store.state.user.lastname.substring(0, 100);
        } else if (key === 'contractorTeamData' && this.isContractorTeamUser) {
          this.contractorTeamData = this.$store.state.user.contractorTeamData;
          this.phone = this.$store.state.user.contractorTeamData?.phone;
        } else {
          this[key] = this.$store.state.user[key];
        }
      });
    },
  },
  methods: {
    ...mapMutations({ preloader: MUTATION.SET_PRELOADER }),
    ...mapActions('contractor', {
      updateContractor: CONTRACTOR_ACTIONS.UPDATE_CONTRACTOR,
    }),
    ...mapActions('user', {
      updateUser: USER_ACTIONS.UPDATE,
      updateEmail: USER_ACTIONS.UPDATE_EMAIL,
    }),
    ...mapActions('partner', { updatePartner: PARTNER_ACTIONS.UPDATE_PARTNER }),
    preparePhone() {
      if (this.contractorTeamData) {
        if ('phone' in this.contractorTeamData) {
          const code = this.contractorTeamData.phone.match(/^\+\d/);
          if (code) {
            this.countryCode = code.join('');
            this.phone = this.contractorTeamData.phone.replace(/^\+\d/, '');
          } else {
            this.phone = this.contractorTeamData.phone;
          }
        } else {
          this.phone = '';
        }
      }
    },
    focusIn() {
      const helpCounter = document.getElementById('help-counter');
      helpCounter.classList.remove('is-invisible');
    },
    focusOut() {
      const helpCounter = document.getElementById('help-counter');
      helpCounter.classList.add('is-invisible');
    },
    submitEmail() {
      return new Promise((resolve, reject) => {
        if (this.email.toLowerCase() === String(this.$store.state.user.email).toLowerCase()) {
          resolve();
          return;
        }
        this.updateEmail({ id: this.id, email: this.email })
          .then((response) => {
            this.isEmailChanged = true;
            resolve(response.message);
          }).catch((error) => {
            if (this.lodash.get(error, 'response.data.error', '') === 'email_already_used') {
              this.$refs.cardProfileObserver.setErrors({
                'Contact Email (Login)*': [''],
              });
            }
            const message = this.lodash.get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com');
            reject(message);
          });
      });
    },
    submitUser() {
      return new Promise((resolve, reject) => {
        if (
          (this.firstname === this.init.firstname
            && this.lastname === this.init.lastname)
            && this.rawContactNumber === this.$store.state.user?.contractorTeamData?.phone
        ) {
          resolve();
          return;
        }
        const payload = this.lodash.pick(this, ['id', 'firstname', 'lastname']);
        if (this.isContractorTeamUser) {
          let phoneNumber = '';
          if (this.rawContactNumber) {
            phoneNumber = this.countryCode + this.rawContactNumber;
          }
          payload.contractorTeamData = JSON.stringify({ phone: phoneNumber });
        }
        this.updateUser(payload)
          .then(() => {
            resolve('Update Firstname Lastname successful!');
          })
          .catch((error) => {
            const message = this.lodash.get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com');
            reject(message);
          });
      });
    },
    submitPhone() {
      return new Promise((resolve, reject) => {
        if (
          (this.isContractorUser && this.rawContactNumber === this.$store.state.contractor.phone)
            || (this.partner.id && this.rawContactNumber === this.$store.state.partner.phone)
        ) {
          resolve();
          return;
        }
        const payload = {
          id: this.partner.id ?? this.contractorId,
          phone: this.rawContactNumber,
        };
        const updateAction = this.contractorId ? this.updateContractor : this.updatePartner;
        updateAction(payload)
          .then(() => {
            this.phone = this.rawContactNumber;
            resolve('Update Phone successful!');
          })
          .catch(() => {
            reject();
          });
      });
    },

    submitProfileSection() {
      this.email = this.email.toLowerCase();
      const changed = Object.keys(this.init).map((key) => {
        if (key === 'phone') {
          return this.rawContactNumber === this.init[key];
        }
        return this[key] === this.init[key];
      }).includes(false);
      if (!changed) {
        this.$buefy.toast.open({ message: 'Update successful!', type: 'is-success' });
        this.setSectionNotEditable();
        return;
      }
      this.$refs.cardProfileObserver.reset();
      const arrayOfPromises = [this.submitEmail(), this.submitUser()];
      if (this.isContractorUser || this.partner.id) {
        arrayOfPromises.push(this.submitPhone());
      }
      this.preloader(true);
      Promise.all(arrayOfPromises)
        .then(() => {
          if (this.isEmailChanged) {
            this.$buefy.toast.open({ message: 'Update successful! We sent an email with a link to confirm changing email.', type: 'is-success' });
          } else {
            this.$buefy.toast.open({ message: 'Update successful!', type: 'is-success' });
          }
          this.init = this.lodash.pick(this, ['firstname', 'lastname', 'email']);
          this.init.phone = this.rawContactNumber;
          this.isEmailChanged = false;
          this.setSectionNotEditable();
        }).catch((message) => {
          if (message) {
            this.$buefy.snackbar.open({
              message,
              actionText: ' ',
              position: 'is-top',
              indefinite: true,
              type: 'is-danger',
            });
          }
        }).finally(() => this.preloader(false));
    },

    setSectionNotEditable(reset = null) {
      if (typeof reset === 'function') reset();
      this.cardFields.forEach((field) => {
        if (field === 'phone') {
          if (this.partner.id) {
            this[field] = this.$store.state.partner[field];
          } else if (this.isContractorTeamUser) {
            this[field] = this.$store.state.user.contractorTeamData[field];
          } else {
            this[field] = this.$store.state.contractor[field];
          }
        } else {
          this[field] = this.$store.state.user[field];
        }
      });
      this.$emit('edit-card', '');
    },
  },
  activated() {
    this.preparePhone();
    this.init = this.lodash.pick(this, ['firstname', 'lastname', 'email', 'phone']);
  },
  deactivated() {
    this.setSectionNotEditable();
  },
};
</script>

<style lang="stylus" scoped>
.column >>> span.icon.is-right.has-text-danger
  height 100%
.control.input-phone
  position relative
.control.input-phone:before
  position absolute
  content var(--country-code)
  top 13px
  left 12px
  z-index 20

.field-input-phone
  >>>.input-phone-bottom
    display flex
    flex-wrap nowrap
    width 100%
    justify-content space-between
  position relative
  flex-wrap wrap
  >>>span.icon.is-right.has-text-danger
    height 100%
  .input-phone
    width 100%
    >>> input
      padding-left 33px
      height 48px
      border-radius 4px!important
      &::placeholder
        color #8a9499

.card-profile-content >>> .b-tooltip::after
  @media screen and (min-width 465px) and (max-width 879px)
    left 35px
.card-profile-content >>> .personal-info__field .label span:after
  white-space pre-line
  text-align left
.card-profile-content >>> .personal-info__field-label
  font-weight 400
  color #8A9499
.card-profile-content >>> .field.personal-info__field input
  height 48px
.card-profile-content
  display flex
  min-height 220px
  height 100%
  flex-direction column
  justify-content space-between
  padding-top 15px
  .field
    color #293033
  >>>input.input
    color #293033
.personal-card-full-name
  font-size 16px
  font-weight 600
  color #525F66
  @media screen and (min-width 1024px)
    font-size 18px
  text-overflow ellipsis
  white-space nowrap
  overflow hidden
  margin-bottom 25px

.personal-card-contacts
  color #525F66
  list-style-type none
  margin-left 15px
  position relative
  >li
    &.email span
      overflow hidden
      white-space nowrap
      text-overflow ellipsis
    display flex
    margin 10px
    position relative
    &.email,&.phone
      span.icon
        left -24px
    span.icon
      position absolute
      top 2px
      left -20px
      >>> i.fa:before
        color rgb(143,167,179)

.personal-card-controls
  display flex
  justify-content right
button.button
  width 135px
  height 48px
  border 1px solid #23D160
  font-size 19px
  text-transform uppercase
.button.is-success
  border none
.button.button-cancel
  color #23D160
.button.button-cancel:hover
  background #17b548
  color white
.loading-overlay >>> .loading-background
  background rgba(255,255,255,0.8)
</style>
